import PropTypes from 'prop-types';
import { css, etiColors } from '@eti/styles';
import { constants } from '@eti/utils';
import { Icon as EtiIcon } from '@eti/components';
import { useTranslation, useDirection } from '@eti/providers';
import {
  ADDED_CHECKED_BAGGAGE,
  INCLUDED_CHECKED_BAGGAGE,
  INFANT_NO_CHECKED_BAGGAGE,
  NO_CHECKED_BAGGAGE,
} from '../../../constants/includedBaggageConstants';
import { getBaggageWeightText } from '../../trip-details/utils/format';

const { unicodeControlCharacters } = constants;
const { LRE, PDF } = unicodeControlCharacters;

export const baggageIconStyles = css`
  margin-right: 10px;

  html[dir='rtl'] & {
    margin-left: 10px;
    margin-right: 0;
  }
`;

const baggageTextStyles = css`
  align-items: center;
  color: ${etiColors.grayScale[10]};
  display: flex;
  font-size: 0.75rem;
  margin-left: auto;

  [dir='rtl'] & {
    margin-left: 0;
    margin-right: auto;
  }
`;

export const BaggageText = ({ children }) => <div className={baggageTextStyles}>{children}</div>;

const IncludedBaggageSummary = ({ baggageDetails, baggageStatus, secondBaggageDetails }) => {
  const { t } = useTranslation();
  const { isRTL } = useDirection();

  const weightText = baggageDetails
    ? `${LRE}${getBaggageWeightText(baggageDetails, secondBaggageDetails, isRTL)}${PDF}`
    : '';

  switch (baggageStatus) {
    case INCLUDED_CHECKED_BAGGAGE:
    case ADDED_CHECKED_BAGGAGE:
      return (
        <BaggageText>
          <EtiIcon
            className={baggageIconStyles}
            color={etiColors.grayScale[8]}
            id="checked-baggage"
            size="12px"
          />
          <span>{`${weightText || ''} ${t('checkInBaggageName')}`}</span>
        </BaggageText>
      );
    case INFANT_NO_CHECKED_BAGGAGE:
      return (
        <BaggageText>
          <EtiIcon
            className={baggageIconStyles}
            color={etiColors.grayScale[8]}
            id="no-baggage"
            size="12px"
          />
          <span>{t('TripDetails.NoCheckedBaggage')}</span>
        </BaggageText>
      );
    case NO_CHECKED_BAGGAGE:
    default:
      return (
        <BaggageText>
          <EtiIcon
            className={baggageIconStyles}
            color={etiColors.grayScale[8]}
            id="no-baggage"
            size="12px"
          />
          <span>{t('TripDetails.BaggageNotIncluded')}</span>
        </BaggageText>
      );
  }
};

const baggageDetailsProps = PropTypes.shape({
  __typename: PropTypes.string,
  baggageDetails: PropTypes.shape({
    __typename: PropTypes.string,
    pieces: PropTypes.any,
    weight: PropTypes.number,
    weightUnit: PropTypes.string,
  }),
  baggageStatus: PropTypes.string,
  travelerId: PropTypes.string,
});

IncludedBaggageSummary.propTypes = {
  baggageDetails: baggageDetailsProps,
  baggageStatus: PropTypes.oneOf([
    ADDED_CHECKED_BAGGAGE,
    INCLUDED_CHECKED_BAGGAGE,
    INFANT_NO_CHECKED_BAGGAGE,
    NO_CHECKED_BAGGAGE,
  ]),
  secondBaggageDetails: PropTypes.shape({
    __typename: PropTypes.string,
    pieces: PropTypes.any,
    weight: PropTypes.number,
    weightUnit: PropTypes.string,
  }),
};

BaggageText.propTypes = {
  children: PropTypes.node,
};

export default IncludedBaggageSummary;
